import { Injectable, inject } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { orderBy } from 'lodash-es';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, take, takeUntil, timeout, withLatestFrom } from 'rxjs/operators';

import {
  SIMILAR_PARAGRAPH_EXPAND_LIMIT,
  SIMILAR_PARAGRAPH_MATCHES_PER_DEAL,
  SIMILAR_PARAGRAPH_RESULT_LIMIT,
  SIMILAR_PARAGRAPH_TIMEOUT_MS,
} from '../../features/deal/constants/limits';
import { SimilarParagraphService } from '../../features/similar-paragraph/services/similar-paragraph.service';
import { similarParagraphSearchErrorMessage } from '../../features/similar-paragraph/utils/similar-paragraph-search-error';
import { AggregatedSelectors } from '../aggregated/aggregated.selector';
import { DealActions } from '../deal/deal.actions';
import { RootState } from '../root-state.model';
import { RootStoreActions } from '../root-store.actions';
import { SimilarParagraphActions } from './similar-paragraph.actions';
import { SimilarParagraphsSelectors } from './similar-paragraph.selectors';
import { TransactionSelectors } from '../transaction/transaction.selectors';

@Injectable()
export class SimilarParagraphsEffects {
  private service = inject(SimilarParagraphService);
  private actions$ = inject(Actions);
  private readonly store = inject<Store<RootState>>(Store);

  search$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(SimilarParagraphActions.SearchSimilarParagraphs),
      switchMap((action) =>
        this.store.select(TransactionSelectors.loadSettled).pipe(
          filter(Boolean),
          take(1),
          timeout({ first: SIMILAR_PARAGRAPH_TIMEOUT_MS, with: () => of(false) }),
          concatLatestFrom(() => [
            this.store,
            this.store.select(AggregatedSelectors.scopedTransactions),
            this.store.select(TransactionSelectors.listLookup),
          ]),
          switchMap(([catalogSettled, store, transactions, listLookup]) => {
            if (!catalogSettled || !store.transaction.loaded) {
              return of(
                SimilarParagraphActions.SearchSimilarParagraphsFail({
                  error: 'The deals could not be loaded. Try again to reload them and search.',
                }),
              );
            }
            const searchText = action.searchText || store.similarParagraph.originalParagraph;
            const transactionIds = (transactions ?? [])
              .filter((t) => t.transactionId !== store.deal.transaction?.transactionId)
              .map((t) => t.transactionId);

            return this.service
              .getSimilars(searchText, transactionIds, store.similarParagraph.groupByDeal ? SIMILAR_PARAGRAPH_MATCHES_PER_DEAL : undefined)
              .pipe(
                map((list) => this.service.createViewModels(list, listLookup)),
                switchMap((viewModels) => this.service.makeDiff(viewModels, searchText)),
                map((list) => orderBy(list, ['score', '_id'], ['desc', 'asc']).slice(0, SIMILAR_PARAGRAPH_RESULT_LIMIT)),
                map((list) => SimilarParagraphActions.SearchSimilarParagraphsSuccess({ list, searchedDealCount: transactionIds.length })),
                timeout(SIMILAR_PARAGRAPH_TIMEOUT_MS),
              );
          }),
          catchError((error: unknown) => {
            console.error(error);
            return of(SimilarParagraphActions.SearchSimilarParagraphsFail({ error: similarParagraphSearchErrorMessage(error) }));
          }),
          takeUntil(this.actions$.pipe(ofType(DealActions.LoadDeal, DealActions.LoadDealSuccess, RootStoreActions.ChangeAsset))),
        ),
      ),
    ),
  );

  regroup$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(SimilarParagraphActions.SetGroupByDeal),
      withLatestFrom(this.store.select(SimilarParagraphsSelectors.originalParagraph)),
      filter(([, originalParagraph]) => !!originalParagraph),
      map(() => SimilarParagraphActions.SearchSimilarParagraphs({})),
    ),
  );

  expandDeal$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(SimilarParagraphActions.ExpandDeal),
      withLatestFrom(this.store.select(SimilarParagraphsSelectors.originalParagraph), this.store.select(TransactionSelectors.listLookup)),
      mergeMap(([{ transactionId }, searchText, listLookup]) =>
        this.service.getSimilars(searchText, [transactionId], SIMILAR_PARAGRAPH_EXPAND_LIMIT).pipe(
          map((paragraphSet) => this.service.createViewModels(paragraphSet, listLookup)),
          switchMap((viewModels) => this.service.makeDiff(viewModels, searchText)),
          map((list) => SimilarParagraphActions.ExpandDealSuccess({ transactionId, list })),
          timeout(SIMILAR_PARAGRAPH_TIMEOUT_MS),
          catchError((error: unknown) => {
            console.error(error);
            return of(SimilarParagraphActions.ExpandDealFail({ transactionId, error: similarParagraphSearchErrorMessage(error) }));
          }),
          takeUntil(
            this.actions$.pipe(
              ofType(
                SimilarParagraphActions.SearchSimilarParagraphs,
                DealActions.LoadDeal,
                DealActions.LoadDealSuccess,
                RootStoreActions.ChangeAsset,
              ),
            ),
          ),
        ),
      ),
    ),
  );
}
